"use client"

export const dynamic = 'force-dynamic'

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Terminal, Download, Upload, Settings, Zap, Code, ArrowRight, CheckCircle, Copy } from "lucide-react"
import Link from "next/link"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"

export default function CLIPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Navigation />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            <Terminal className="w-3 h-3 mr-1" />
            Command Line Interface
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Locsy CLI
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Streamline your translation workflow with our powerful command-line interface. Perfect for CI/CD pipelines and automated deployments.
          </p>
        </div>

        {/* Quick Start */}
        <div className="max-w-4xl mx-auto mb-20">
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5 text-blue-600" />
                Quick Start
              </CardTitle>
              <CardDescription>
                Get up and running with the Locsy CLI in minutes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">1. Install the CLI</h3>
                  <div className="bg-muted rounded-lg p-4 font-mono text-sm">
                    <div className="flex items-center justify-between">
                      <span>npm install -g @locsy/cli</span>
                      <Button variant="ghost" size="sm">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">2. Login to your account</h3>
                  <div className="bg-muted rounded-lg p-4 font-mono text-sm">
                    <div className="flex items-center justify-between">
                      <span>locsy login</span>
                      <Button variant="ghost" size="sm">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">3. Start translating</h3>
                  <div className="bg-muted rounded-lg p-4 font-mono text-sm">
                    <div className="flex items-center justify-between">
                      <span>locsy translate en.json --to es,fr,de</span>
                      <Button variant="ghost" size="sm">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Command Reference */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Command Reference</h2>
          
          <Tabs defaultValue="auth" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="auth">Authentication</TabsTrigger>
              <TabsTrigger value="translate">Translate</TabsTrigger>
              <TabsTrigger value="projects">Projects</TabsTrigger>
              <TabsTrigger value="files">Files</TabsTrigger>
              <TabsTrigger value="config">Config</TabsTrigger>
            </TabsList>

            <TabsContent value="auth" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Authentication Commands</CardTitle>
                  <CardDescription>Manage your authentication with the Locsy service</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-2">locsy login</h4>
                    <p className="text-sm text-muted-foreground mb-2">Authenticate with your Locsy account</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy login [--api-key YOUR_API_KEY]
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      <strong>Options:</strong>
                      <ul className="list-disc list-inside mt-1">
                        <li>--api-key: Use API key authentication instead of browser login</li>
                        <li>Alternatively, set LOCSY_API_KEY environment variable</li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy logout</h4>
                    <p className="text-sm text-muted-foreground mb-2">Remove stored authentication credentials</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy logout
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy whoami</h4>
                    <p className="text-sm text-muted-foreground mb-2">Display current user information</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy whoami
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="translate" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Translation Commands</CardTitle>
                  <CardDescription>Translate your files using AI-powered translation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-2">locsy translate</h4>
                    <p className="text-sm text-muted-foreground mb-2">Translate files to target languages</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy translate &lt;file&gt; --to &lt;languages&gt; [options]
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      <strong>Options:</strong>
                      <ul className="list-disc list-inside mt-1">
                        <li>--to: Target languages (comma-separated, e.g., es,fr,de)</li>
                        <li>--project: Project ID to associate translations with</li>
                        <li>--output: Output directory for translated files</li>
                        <li>--format: Output format (preserve, flat, nested)</li>
                        <li>--glossary: Use project glossary for consistency</li>
                        <li>--context: Additional context for translation</li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Examples</h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm mb-1">Basic translation:</p>
                        <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                          locsy translate en.json --to es,fr
                        </div>
                      </div>
                      <div>
                        <p className="text-sm mb-1">With custom output directory:</p>
                        <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                          locsy translate src/locales/en.json --to de --output ./translations
                        </div>
                      </div>
                      <div>
                        <p className="text-sm mb-1">Using project context:</p>
                        <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                          locsy translate app.json --to ja,ko --project my-app --glossary
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="projects" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Project Management</CardTitle>
                  <CardDescription>Manage your translation projects</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-2">locsy projects</h4>
                    <p className="text-sm text-muted-foreground mb-2">List all your projects</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy projects [--format json|table]
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy project create</h4>
                    <p className="text-sm text-muted-foreground mb-2">Create a new project</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy project create &lt;name&gt; [--description "Project description"]
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy project info</h4>
                    <p className="text-sm text-muted-foreground mb-2">Get detailed project information</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy project info &lt;project-id&gt;
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy project delete</h4>
                    <p className="text-sm text-muted-foreground mb-2">Delete a project</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy project delete &lt;project-id&gt; [--yes]
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="files" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>File Management</CardTitle>
                  <CardDescription>Upload, download, and manage translation files</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-2">locsy upload</h4>
                    <p className="text-sm text-muted-foreground mb-2">Upload source files to a project</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy upload &lt;files...&gt; --project &lt;project-id&gt;
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy download</h4>
                    <p className="text-sm text-muted-foreground mb-2">Download translated files</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy download &lt;file-id&gt; [--language &lt;lang&gt;] [--output &lt;dir&gt;]
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy files</h4>
                    <p className="text-sm text-muted-foreground mb-2">List files in a project</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy files --project &lt;project-id&gt;
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy sync</h4>
                    <p className="text-sm text-muted-foreground mb-2">Sync local files with project</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy sync [--push|--pull] [--project &lt;project-id&gt;]
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="config" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Configuration</CardTitle>
                  <CardDescription>Manage CLI settings and defaults</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-2">locsy config</h4>
                    <p className="text-sm text-muted-foreground mb-2">View current configuration</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy config [--global|--local]
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy config set</h4>
                    <p className="text-sm text-muted-foreground mb-2">Set configuration values</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy config set &lt;key&gt; &lt;value&gt; [--global]
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      <strong>Available keys:</strong>
                      <ul className="list-disc list-inside mt-1">
                        <li>default-project: Default project ID</li>
                        <li>output-format: Default output format</li>
                        <li>default-languages: Default target languages</li>
                        <li>api-endpoint: API endpoint URL</li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">locsy init</h4>
                    <p className="text-sm text-muted-foreground mb-2">Initialize project configuration</p>
                    <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                      locsy init [--interactive]
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Creates a <code className="bg-muted px-1 rounded">.locsyrc.json</code> file in the current directory
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* CI/CD Integration */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">CI/CD Integration</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5 text-blue-600" />
                  GitHub Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-muted rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <pre>{`name: Locsy Translation
on:
  push:
    branches: [main]
    paths: ['src/locales/en.json']

jobs:
  translate:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with:
          node-version: '18'
      
      - name: Install Locsy CLI
        run: npm install -g @locsy/cli
      
      - name: Translate files
        env:
          LOCSY_API_KEY: $\{{ secrets.LOCSY_API_KEY }}
          LOCSY_PROJECT: $\{{ secrets.LOCSY_PROJECT }}
        run: |
          locsy translate src/locales/en.json \\
            --to es,fr,de \\
            --project $LOCSY_PROJECT
        
      - name: Commit translations
        run: |
          git config --local user.email "action@github.com"
          git config --local user.name "GitHub Action"
          git add src/locales/
          git commit -m "Update translations" || exit 0
          git push`}</pre>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-green-600" />
                  Configuration File
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Create a <code className="bg-muted px-1 rounded">.locsyrc.json</code> file to set project defaults:
                </p>
                <div className="bg-muted rounded-lg p-4 font-mono text-sm">
                  <pre>{`{
  "project": "my-app-project-id",
  "defaultLanguages": ["es", "fr", "de"],
  "sourceFile": "src/locales/en.json",
  "outputDir": "src/locales",
  "format": "nested",
  "glossary": true,
  "context": "Web application UI"
}`}</pre>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  <strong>Environment Variables:</strong>
                </p>
                <div className="bg-muted rounded-lg p-4 font-mono text-sm mt-2">
                  <pre>{`# Set API key via environment variable
export LOCSY_API_KEY="your-api-key-here"

# Optional: Set default project
export LOCSY_PROJECT="your-project-id"`}</pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Features Grid */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">CLI Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader>
                <Zap className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle className="text-lg">Lightning Fast</CardTitle>
                <CardDescription className="text-sm">
                  Optimized for speed with parallel processing and caching
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-l-4 border-l-green-500">
              <CardHeader>
                <Upload className="h-8 w-8 text-green-600 mb-2" />
                <CardTitle className="text-lg">Batch Operations</CardTitle>
                <CardDescription className="text-sm">
                  Process multiple files and languages simultaneously
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardHeader>
                <Terminal className="h-8 w-8 text-purple-600 mb-2" />
                <CardTitle className="text-lg">Pipeline Ready</CardTitle>
                <CardDescription className="text-sm">
                  Built for CI/CD with proper exit codes and JSON output
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to automate your translations?</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Install the Locsy CLI today and streamline your localization workflow
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link href="/auth/signup">
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link href="/docs">View Full Docs</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
} 